<template>
  <div class="lesson-view-container">
    <!-- 筛选区域 -->
    <filter-selector
      ref="filterSelector"
      :teacherId="teacherId"
      :institutionId="institutionId"
      :roles="roles"
      :includeLessonSelector="true"
      :initial-filters="initialFilters"
      @filter-change="handleFilterChange"
      @classes-loaded="handleClassesLoaded"
      @lessons-loaded="handleLessonsLoaded"
    />

    <!-- 数据概览 -->
    <div class="data-overview" v-if="filters.lesson">
      <div class="semester-info">
        {{ lessonDataTitle }}
      </div>
      <div class="stats">
        本讲班级平均正确率: {{ averageAccurateRate }}%
        <span class="pending-review" @click="handlePendingReviewClick">待复评: {{ needReviewQuestionNum }}</span>
      </div>
      <div class="actions">
        <a-button type="primary" @click="handleExportExcel">
          <a-icon type="download" />
          导出当前表格
        </a-button>
        <a-button type="primary" @click="handleBatchPrint" style="margin-left: 10px">
          <a-icon type="printer" />
          打印本班本讲错题
        </a-button>
      </div>
    </div>

    <!-- 表格展示区域 -->
    <div class="table-container" v-if="filters.lesson">
      <a-table
        :columns="columns"
        :dataSource="tableData"
        :pagination="false"
        :scroll="{ x: 'max-content', y: '40vh' }"
        :rowKey="record => record.id"
        bordered
      >
        <!-- 使用 scopedSlots 自定义列 -->
        <template slot="index" slot-scope="text, record, index">
          {{ index + 1 }}
        </template>

        <!-- 姓名列 -->
        <template slot="name" slot-scope="text, record">
          <a class="student-link" @click="handleNameClick(record)">{{ text }}</a>
        </template>

        <template slot="correctRate" slot-scope="text">
          <div :class="getRateClass(text)">{{ text }}%</div>
        </template>

        <template v-for="config in msTopicBasicInfoVoList" v-slot:[config.key]="text, record">
          <div :key="config.key + '_' + record.id">
            <!-- 处理汇总文本数据（类型 1、2、5） -->
            <div v-if="text && text.type === 'summaryText'" class="summary-text">
              <span>对{{ text.correctCount }}题</span>
              <span class="total-count">（共{{ text.totalCount }}题）</span>
            </div>
            <!-- 处理综合题小题列表数据（类型 4） -->
            <div v-else-if="text && text.type === 'smallQuestionList'" class="small-question-list">
              <div v-for="(item, index) in text.data" :key="index" class="small-question-item">
                <span class="small-question-index">({{ index + 1 }})</span>
                <a-icon v-if="item === 4" type="check" class="correct-icon" />
                <a-icon v-else-if="item === 1" type="check" class="corrected-icon" />
                <a-icon v-else-if="item === 0" type="close" class="wrong-icon" />
                <span v-else-if="item === -1" class="unfinished">--</span>
              </div>
            </div>
            <!-- 处理原有数据（类型 0 练习题） -->
            <template v-else>
              <a-icon v-if="text === 'correct'" type="check" class="correct-icon" />
              <a-icon v-else-if="text === 'corrected'" type="check" class="corrected-icon" />
              <a-icon v-else-if="text === 'wrong'" type="close" class="wrong-icon" />
              <span v-else-if="text === 'PanWrong'" class="judge-failed">判题失败</span>
              <div v-else-if="text && text.includes('correct_')" class="partial-correct">
                <a-icon type="check" class="correct-icon" />{{ text.split("_")[1] }}题
              </div>
              <span v-else-if="text === 'none'">--</span>
              <span v-else>{{ text }}</span>
            </template>
          </div>
        </template>

      <template slot="action" slot-scope="text, record">
        <div class="vertical-actions">
          <a class="student-link"
            :class="{'disabled-link': record.hasReport !== 1}"
            @click="record.hasReport === 1 && showReport(record)">查看报告</a>
          <a class="student-link"
            :class="{'disabled-link': record.hasMistakeBook !== 1}"
            @click="record.hasMistakeBook === 1 && printMistakes(record)">打印本讲错题</a>
        </div>
      </template>
      </a-table>
    </div>

    <!-- 空状态提示 -->
    <div v-else class="empty-state">
      <a-empty description="请选择筛选条件查看数据" />
    </div>
    <!-- 学习报告 -->
    <ReportSidebar
      :visible="showingReport"
      :url="dynamicUrl"
      :report="reportUrl"
      :title="`${currentStudentName} 学习报告`"
      @close="closeReport"
    />
  </div>
</template>

<script>
import axios from 'axios';
import {
  Table, Icon, Empty, Button, message,
} from 'ant-design-vue';
import beike from '@/service/beike';
import ReportSidebar from './ReportSidebar.vue';
import FilterSelector from './common/FilterSelector.vue';

export default {
  name: 'LessonView',
  components: {
    ATable: Table,
    AIcon: Icon,
    AEmpty: Empty,
    AButton: Button,
    ReportSidebar,
    FilterSelector,
  },
  props: {
    institutionId: {
      type: [String, Number],
      default: null,
    },
    teacherId: {
      type: [String, Number],
      default: null,
    },
    roles: {
      type: Array,
      default: () => [],
    },
    initialFilters: {
      type: Object,
      default: () => ({
        term: '',
        grade: '',
        classTime: '',
        lesson: '',
      }),
    },
  },
  data() {
    return {
      // 筛选条件
      filters: {
        term: '',
        grade: '',
        classTime: '',
        lesson: '',
      },
      dynamicUrl: '', // 用于传递给ReportSidebar组件的动态URL
      showingReport: false,
      reportUrl: '',
      currentStudentName: '',
      // 接口返回的数据字段，直接与接口返回对应
      averageAccurateRate: 0, // 班级平均正确率
      firstAverageAccurateRate: 0, // 首答平均正确率
      needReviewQuestionNum: 0, // 待复评数量
      msReviewStudentInfoVoList: [], // 复评学生信息
      msTopicBasicInfoVoList: [], // 题目基本信息列表
      studentAnswerReviewInfoVoList: [], // 学生答题信息列表
      // 当前班级、讲次信息
      currentClass: {
        name: '',
      },
      currentLessonNumber: '',
      // 表格数据
      tableData: [],
      // 选项数据
      classOptions: [],
      lessonOptions: [],
    };
  },
  computed: {
    // 计算属性
    currentSemesterText() {
      if (this.filters.termLabel) {
        return this.filters.termLabel;
      }
      return '';
    },
    lessonDataTitle() {
      return `${this.filters.termLabel}-${this.currentClass.name}-第${this.filters.lessonNum || ''}讲课堂数据`;
    },
    shouldFixActionColumn() {
      // 根据练习列数量和屏幕宽度等条件判断是否需要固定操作列
      return this.msTopicBasicInfoVoList.length > 4;
    },

    // 动态生成表格列
    columns() {
      const columns = [
        {
          title: '序号',
          dataIndex: 'index',
          key: 'index',
          width: 60,
          fixed: 'left',
          scopedSlots: { customRender: 'index' },
        },
        {
          title: '姓名',
          dataIndex: 'name',
          key: 'name',
          width: 120,
          fixed: 'left',
          scopedSlots: { customRender: 'name' }, // 添加自定义渲染
        },
        {
          title: '本讲正确率',
          dataIndex: 'lessonAccuracy',
          key: 'correctRate',
          width: 120,
          scopedSlots: { customRender: 'correctRate' },
        },
      ];

      // 添加练习列
      this.msTopicBasicInfoVoList.forEach((config, index) => {
        columns.push({
          title: this.getPracticeTitle(config, index),
          dataIndex: `question_${index + 1}`,
          key: `question_${index + 1}`,
          width: 160,
          scopedSlots: { customRender: `question_${index + 1}` },
        });
      });

      // 添加操作列
      columns.push({
        title: '操作',
        dataIndex: 'action',
        key: 'action',
        fixed: this.shouldFixActionColumn ? 'right' : false,
        width: 150,
        scopedSlots: { customRender: 'action' },
        customHeaderCell: () => ({ class: 'custom-action-header' }), // 添加自定义类名
      });

      return columns;
    },
  },
  mounted() {
    // 应用初始筛选条件（如果有）
    if (this.initialFilters) {
      this.filters = { ...this.initialFilters };
      // 如果筛选条件中有班级和讲次，则获取数据
      if (this.filters.classTime && this.filters.lesson) {
        this.fetchLessonData();
      }
    }
  },
  methods: {
    handleFilterChange(filters) {
      this.filters = { ...filters };

      // 发送筛选条件变更事件到父组件
      this.$emit('filter-change', this.filters);

      // 如果选择了讲次，则获取讲次数据
      if (this.filters.lesson && this.filters.classTime) {
        this.fetchLessonData();
      }
    },
    // 生成练习列标题
    getPracticeTitle(config, index) {
      return () => {
        const h = this.$createElement;
        return h('div', [
          h('div', { style: 'font-weight: bold; font-size: 16px;' }, config.topicName || `练习${index + 1}`),
          h('div', { style: 'font-size: 10px; color: #505873;' }, `首答正确率：${config.firstLessonAccuracy || 0}%`),
          h('div', { style: 'font-size: 10px; color: #505873;' }, `班级正确率：${config.averageAccuracy || 0}%`),
        ]);
      };
    },

    // 处理班级列表加载完成事件
    handleClassesLoaded(classes) {
      this.classOptions = classes;
    },

    // 处理讲次列表加载完成事件
    handleLessonsLoaded(lessons) {
      this.lessonOptions = lessons;
    },

    // 获取讲次数据
    async fetchLessonData() {
      if (!this.filters.lesson || !this.filters.classTime) return;
      try {
        const classValue = JSON.parse(this.filters.classTime);
        const params = {
          classId: classValue.classId,
          lessonId: this.filters.lesson,
        };
        // 调用接口
        const result = await beike.getBkLessonView(params);
        // 关闭加载提示
        message.destroy();
        // 处理返回数据 - 修正数据结构处理
        if (result) {
          const data = result; // 直接使用返回结果，而不是 result.body
          // 更新班级平均正确率和待复评数
          this.averageAccurateRate = data.averageAccurateRate || 0;
          this.firstAverageAccurateRate = data.firstAverageAccurateRate || 0;
          this.needReviewQuestionNum = data.msReviewStudentInfoVoList.length || 0;
          // 更新题目信息列表
          if (data.msTopicBasicInfoVoList && Array.isArray(data.msTopicBasicInfoVoList)) {
            this.msTopicBasicInfoVoList = data.msTopicBasicInfoVoList.map((topic, index) => ({
              ...topic,
              key: `question_${index + 1}`, // 添加key字段用于表格渲染
            }));
          } else {
            this.msTopicBasicInfoVoList = [];
          }
          this.msReviewStudentInfoVoList = data.msReviewStudentInfoVoList || [];
          // 更新学生答题信息列表和表格数据
          if (data.studentAnswerReviewInfoVoList && Array.isArray(data.studentAnswerReviewInfoVoList)) {
            this.studentAnswerReviewInfoVoList = data.studentAnswerReviewInfoVoList;
            // 处理表格数据
            this.tableData = data.studentAnswerReviewInfoVoList.map((student) => {
              // 基本学生信息
              const studentData = {
                id: student.studentUserId,
                name: student.studentName,
                lessonAccuracy: student.lessonAccuracy || 0,
                firstLessonAccuracy: student.firstLessonAccuracy || 0,
                hasReport: student.hasReport || 0,
                hasMistakeBook: student.hasMistakeBook || 0,
              };
              // 处理学生答题数据
              if (student.studentAnswerTopicInfoVos && Array.isArray(student.studentAnswerTopicInfoVos)) {
                student.studentAnswerTopicInfoVos.forEach((answer, index) => {
                  if (index < this.msTopicBasicInfoVoList.length) {
                    const key = `question_${index + 1}`;
                    const pushType = answer.pushType || 0;
                    // 类型 1、2、5：快问快答、单步训练、问答连连 - 使用汇总文本展示
                    if ([1, 2, 5].includes(pushType) && answer.smallQuestionTotalCount && Array.isArray(answer.smallQuestionTotalCount)) {
                      const totalCount = answer.smallQuestionTotalCount.length;
                      const correctCount = answer.smallQuestionCorrectCount;
                      studentData[key] = {
                        type: 'summaryText',
                        text: `对${correctCount}题（共${totalCount}题）`,
                        correctCount,
                        totalCount,
                      };
                    } else if (pushType === 4 && answer.smallQuestionTotalCount && Array.isArray(answer.smallQuestionTotalCount) && answer.smallQuestionTotalCount.length > 0) {
                      studentData[key] = {
                        type: 'smallQuestionList',
                        data: answer.smallQuestionTotalCount,
                        state: answer.state,
                      };
                    } else {
                      // 根据题目答题状态设置对应的值
                      switch (answer.state) {
                        case 5: // 判题失败
                          studentData[key] = 'PanWrong';
                          break;
                        case 4: // 正确
                          studentData[key] = 'correct';
                          break;
                        case 0: // 错误
                          studentData[key] = 'wrong';
                          break;
                        case 1: // 订正对
                          if (answer.smallQuestionCorrectCount === 0) {
                            studentData[key] = 'correct';
                          } else {
                            studentData[key] = 'corrected';
                          }
                          break;
                        case 2: // 半对半错
                          if (answer.smallQuestionCorrectCount === -1) {
                            studentData[key] = 'wrong';
                          } else if (answer.smallQuestionCorrectCount > 0) {
                            studentData[key] = `correct_${answer.smallQuestionCorrectCount}`;
                          } else {
                            studentData[key] = 'corrected';
                          }
                          break;
                        case -1: // 未完成
                        default:
                          studentData[key] = 'none';
                          break;
                      }
                    }
                  }
                });
              }
              return studentData;
            });
          } else {
            this.studentAnswerReviewInfoVoList = [];
            this.tableData = [];
          }
          // 获取讲次名称
          if (this.lessonOptions && this.lessonOptions.length > 0) {
            const currentLesson = this.lessonOptions.find((lesson) => lesson.value === this.filters.lesson);
            if (currentLesson) {
              this.currentLessonNumber = this.filters.lessonNum || '';
            }
          }
          // 获取班级名称
          if (this.classOptions && this.classOptions.length > 0) {
            const currentClass = this.classOptions.find((cls) => {
              const clsValue = JSON.parse(cls.value);
              return clsValue.classId === classValue.classId;
            });
            if (currentClass) {
              this.currentClass.name = currentClass.label.split(' ')[0] || '';
            }
          }
        } else {
          message.error('获取讲次视角数据失败：返回结果为空');
        }
      } catch (error) {
        // 关闭加载提示
        message.destroy();
        message.error('获取讲次视角数据失败');
      }
    },

    getRateClass(rate) {
      if (rate >= 90) return 'rate-excellent';
      if (rate >= 70) return 'rate-good';
      return 'rate-needs-improvement';
    },
    handleNameClick(record) {
      const prefix = process.env.NODE_ENV === 'development' ? '' : '/next';
      // 解析classTime字符串为对象
      const classValue = JSON.parse(this.filters.classTime);
      const url = `${prefix}/dataInfo.html#/detail?classId=${classValue.classId}&lessonId=${this.filters.lesson}&studentUserId=${record.id}&studentName=${record.name}&fromView=lesson`;
      window.open(url, '_blank');
    },

    async showReport(record) {
      this.currentStudentName = record.name;
      const classValue = JSON.parse(this.filters.classTime);
      const classCodeData = await beike.getClassCode(classValue.classId);
      const params = new URLSearchParams({
        subject: '思维',
        userId: record.id,
        classId: classValue.classId || '',
        classCode: classCodeData.classCode || '',
        teacherPhone: this.teacherId || '',
        lessonNum: this.filters.lessonNum || '',
        lessonId: this.filters.lesson || '',
        institutionId: this.institutionId || '',
        cardCode: record.cardCode || '',
        timestamp: Date.now(),
        noPJ: true,
      });
      const req = {
        appId: 'wxea82db597fe97be9',
        path: 'pages/study/reportview/index',
        queryParam: params.toString(),
        envVersion: 'release', // 正式版为:release 体验版:trial 开发版:develop
      };

      try {
        // 使用axios直接发送请求，绕过fetch.ts的处理
        const { data: apiResponse } = await axios({
          url: window.__ENV__ === 'production' ? 'https://atlas.aixuexi.com/atlas/wxMini/qrCodePath' : 'https://sq-private-dev2.ghost.aixuexi.com/atlas/wxMini/qrCodePath',
          method: 'post',
          data: req,
          headers: {
            'Content-Type': 'application/json',
            'Atlas-Token': 'EVKgR0eb',
            'Atlas-Key': '100010',
          },
        });

        // 直接从apiResponse中获取data字段
        const qrCodeData = apiResponse && apiResponse.data;

        if (qrCodeData) {
          // 成功处理逻辑
          let baseUrl;
          if (window.__ENV__ === 'production') {
            // 线上环境
            baseUrl = 'https://bxstudent.gaosiedu.com/bx-report-h5/report';
          } else {
            // 测试环境或其他环境
            baseUrl = 'https://sq-private-dev2-reserp.ghost.aixuexi.com/bx-report-h5/report';
          }

          this.reportUrl = `${baseUrl}?${params.toString()}&studentCode=`;
          // 设置二维码URL到dynamicUrl，供ReportSidebar组件使用
          this.dynamicUrl = qrCodeData.path;
          this.showingReport = true;
        } else {
          message.error('二维码数据获取失败');
        }
      } catch (error) {
        // 错误处理
        message.error('获取二维码URL失败');
      }
    },
    // 关闭报告
    closeReport() {
      this.showingReport = false;
    },
    printMistakes(record) {
      message.info(`正在打印${record.name}的本讲错题`);

      // 解析班级信息
      const classValue = JSON.parse(this.filters.classTime);
      const { classId } = classValue;
      const lessonId = this.filters.lesson;

      const prefix = process.env.NODE_ENV === 'development' ? '' : '/next';
      // 构造打开新页面的URL，直接跳转到external页面
      const url = `${prefix}/dataInfo.html#/external?classId=${classId}&lessonId=${lessonId}&studentUserId=${record.id}&studentName=${encodeURIComponent(record.name)}&mode=single&fromView=lesson`;

      window.open(url, '_blank');
    },
    async handleExportExcel() {
      const classValue = JSON.parse(this.filters.classTime);
      const params = {
        classId: classValue.classId,
        lessonId: this.filters.lesson,
        dataTitle: this.lessonDataTitle,
        term: this.term,
      };
      const res = await beike.exportMsClassLessonReviewInfoNew(params);
      message.success('表格导出成功', res);
    },
    handleBatchPrint() {
      message.info('正在准备批量打印...');
      try {
        // 解析班级信息
        const classValue = JSON.parse(this.filters.classTime);
        const { classId } = classValue;
        const { lesson: lessonId, lessonName } = this.filters;

        // 将学生列表转换为JSON字符串，并进行URL编码
        const studentList = this.tableData.map((student) => ({
          studentUserId: student.id,
          name: student.name,
        }));
        const studentListParam = encodeURIComponent(JSON.stringify(studentList));
        // 构建跳转URL
        const prefix = process.env.NODE_ENV === 'development' ? '' : '/next';
        const url = `${prefix}/dataInfo.html#/external?classId=${classId}&lessonId=${lessonId}&lessonName=${lessonName}&mode=multiple&studentList=${studentListParam}&fromView=lesson`;
        // 跳转到批量打印页面
        window.open(url, '_blank');
      } catch (error) {
        message.error('批量打印跳转失败，请重试');
      }
    },
    // 处理点击"待复评"的事件
    handlePendingReviewClick() {
      if (this.needReviewQuestionNum <= 0) {
        message.info('当前没有待复评的题目');
        return;
      }

      // 获取当前班级和讲次信息
      if (!this.filters.classTime || !this.filters.lesson) {
        message.error('请先选择班级和讲次');
        return;
      }

      try {
        // 解析班级信息
        const classValue = JSON.parse(this.filters.classTime);
        const { classId } = classValue;
        const lessonId = this.filters.lesson;

        // 处理待复评学生数据，确保格式符合detail页面需要的学生列表格式
        const studentList = this.msReviewStudentInfoVoList.map((student) => ({
          studentUserId: student.studentUserId,
          studentName: student.studentName,
        }));
        // 将学生列表转换为JSON字符串并进行URL编码
        const studentListParam = encodeURIComponent(JSON.stringify(studentList));

        // 构建跳转URL
        const prefix = process.env.NODE_ENV === 'development' ? '' : '/next';
        const url = `${prefix}/dataInfo.html#/detail?classId=${classId}&lessonId=${lessonId}&fromPendingReview=true&fromView=lesson&studentList=${studentListParam}`;

        // 跳转到详情页面
        window.open(url, '_blank');
      } catch (error) {
        message.error('跳转失败，请重试');
      }
    },

  },
};
</script>

<style lang="scss" scoped>
.lesson-view-container {
  .summary-text {
    .total-count {
      font-size: 12px;
      color: #999999;
      margin-left: 4px;
    }
}
  .small-question-list {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 5px;
  max-height: 52px;
  line-height: 24px;
  overflow-y: hidden;
}

.small-question-item {
  display: flex;
  align-items: center;
  margin-bottom: 2px;
  margin-right: 5px;
  line-height: 24px;
}

.small-question-index {
  margin-right: 5px;
  font-size: 12px;
  color: #505873;
}

.unfinished {
  color: #999999;
  font-size: 12px;
}
.data-overview {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding: 0 10px;

    .semester-info {
      width: 420px;
    font-size: 16px;
    font-weight: bold;
    color: #505873;
    }

    .stats {
    font-size: 14px;
    color: #505873;

    .pending-review {
        margin-left: 20px;
        color: #ff4d4f;
        &:hover {
        cursor: pointer;
        }
    }
    }
}

.lesson-view-container ::v-deep .ant-table-thead {
  border-radius: 0;
}

::v-deep .ant-table-fixed {
  border: 0;
}

::v-deep .ant-table-fixed-left,
::v-deep .ant-table-fixed-right {
  z-index: 0 !important;
  border-radius: 0;
  box-shadow: none;
}

::v-deep .ant-table-fixed-right {
  right: 10px;
  width: 150px;
}

::v-deep .ant-table-body-outer {
  height: calc(40vh - 6px) !important;
  max-height: calc(40vh - 6px) !important;
  margin-bottom: 0 !important;
  padding-bottom: 0 !important;
}

::v-deep .ant-table-body-inner {
  overflow: hidden !important;
}

.table-container {
    ::v-deep .ant-table-thead > tr > th {
    background-color: #f2f7fc;
    text-align: center;
    border-radius: 0;
    }
    ::v-deep .ant-table-body {
      &::-webkit-scrollbar {
      width: 6px;
      height: 6px;
    }
    }

    ::v-deep .ant-table {
    // 确保表格宽度计算准确
    width: 100% !important;
   }

   ::v-deep .ant-table table {
    width: auto;
   }

   ::v-deep .ant-table-scroll table {
    min-width: auto !important;
    table-layout: fixed;
   }

    ::v-deep .ant-table-tbody > tr > td:not(:last-child) {
      border-right: 1px solid #e8e8e8;
    }
    ::v-deep .ant-table-row-cell-last {
    border-right: 1px solid #e8e8e8 !important;
    height: auto !important;
    }

    ::v-deep .ant-table-header {
    background-color: #ffffff;
    box-shadow: none;
    }

    ::v-deep .ant-table-tbody > tr > td {
    text-align: center;
    }

    .correct-icon {
    color: #06c270;
    }

    .corrected-icon {
    color: #fdac42;
    }

    .wrong-icon {
    color: #ff4d4f;
    }

    .judge-failed {
      color: #999999;
      font-size: 12px;
    }

    .partial-correct {
    display: flex;
    align-items: center;
    justify-content: center;

    .correct-icon {
        margin-right: 4px;
    }
    }

    .rate-excellent {
    width: 50px;
    margin: auto;
    background-color: #06c270;
    color: #ffffff;
    font-weight: bold;
    border-radius: 4px;
    }

    .rate-good {
      width: 50px;
      margin: auto;
      background-color: #fdac42;
      color: #ffffff;
      font-weight: bold;
      border-radius: 4px;
    }

    .rate-needs-improvement {
      width: 50px;
      margin: auto;
      background-color: #f6c3c4;
      color: #f84444;
      font-weight: bold;
      border-radius: 4px;
    }

    .student-link {
      color: #1890ff;
      cursor: pointer;

      &:hover {
        color: #40a9ff;
        // text-decoration: underline;
      }
    }
}
.actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}
.vertical-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  a {
    display: block;
    margin-bottom: 8px;
    &:last-child {
      margin-bottom: 0;
    }
  }
  .disabled-link {
    color: #cccccc !important;
    cursor: not-allowed !important;
  }
  .disabled-link:hover {
    color: #cccccc !important;
    text-decoration: none !important;
  }

}

.empty-state {
    padding: 40px 0;
    text-align: center;
}
}
</style>
